import React, { useState, useEffect } from 'react';
import { SPM3_REGIONS, SPM_FIGURE_METADATA, SPM3Region } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Sun, CloudRain, Flame, Play, Pause, Sparkles, Navigation } from 'lucide-react';

interface SPM3ExtremesProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

type ExtremeType = 'hot' | 'precip' | 'drought';

export const SPM3Extremes: React.FC<SPM3ExtremesProps> = ({ isPlaying, onTogglePlay }) => {
  const meta = SPM_FIGURE_METADATA['spm3'];
  const audioEngine = AudioEngine.getInstance();

  const [activeType, setActiveType] = useState<ExtremeType>('hot');
  const [activeRegionId, setActiveRegionId] = useState<string | null>(null);
  const [scanIndex, setScanIndex] = useState<number>(0);

  // Spatial scanning loop when playing
  useEffect(() => {
    let timer: number;
    if (isPlaying) {
      timer = window.setInterval(() => {
        setScanIndex((prev) => {
          const next = (prev + 1) % SPM3_REGIONS.length;
          triggerRegionAudio(SPM3_REGIONS[next]);
          return next;
        });
      }, 500);
    }
    return () => clearInterval(timer);
  }, [isPlaying, activeType]);

  const triggerRegionAudio = (region: SPM3Region) => {
    if (!audioEngine.getIsUnlocked()) return;

    let increaseVal: string = 'high';
    if (activeType === 'hot') increaseVal = region.hotIncrease;
    else if (activeType === 'precip') increaseVal = region.precipIncrease;
    else increaseVal = region.droughtIncrease;

    // Pitch & Intensity mapping based on increase severity
    let pitch = 440; // A4 default
    if (increaseVal === 'high') pitch = 784; // G5
    else if (increaseVal === 'medium') pitch = 587; // D5
    else pitch = 392; // G4

    // Stereo Panning from West (-0.8) to East (+0.8) based on X position
    const pan = ((region.x - 2) / 9) * 1.6 - 0.8;

    audioEngine.triggerExtremeTransient(pitch, pan, increaseVal === 'high' ? 0.8 : 0.4);
  };

  const handleRegionClick = (region: SPM3Region) => {
    setActiveRegionId(region.id);
    triggerRegionAudio(region);
  };

  return (
    <div className="space-y-6">
      {/* Title Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>

        {/* Extreme Type Filter & Spatial Play */}
        <div className="flex items-center flex-wrap gap-3">
          <div className="flex items-center gap-1 bg-slate-900 p-1 rounded-xl border border-slate-800 text-xs">
            <button
              onClick={() => setActiveType('hot')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold transition-all ${
                activeType === 'hot' ? 'bg-red-500 text-slate-950 glow-red' : 'text-slate-400 hover:text-white'
              }`}
            >
              <Sun className="w-3.5 h-3.5" />
              <span>Hot Extremes</span>
            </button>
            <button
              onClick={() => setActiveType('precip')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold transition-all ${
                activeType === 'precip' ? 'bg-cyan-500 text-slate-950 glow-cyan' : 'text-slate-400 hover:text-white'
              }`}
            >
              <CloudRain className="w-3.5 h-3.5" />
              <span>Heavy Precip</span>
            </button>
            <button
              onClick={() => setActiveType('drought')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold transition-all ${
                activeType === 'drought' ? 'bg-amber-500 text-slate-950 glow-amber' : 'text-slate-400 hover:text-white'
              }`}
            >
              <Flame className="w-3.5 h-3.5" />
              <span>Drought</span>
            </button>
          </div>

          <button
            onClick={onTogglePlay}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-lg ${
              isPlaying ? 'bg-amber-500 text-slate-950 glow-amber' : 'bg-cyan-500 text-slate-950 glow-cyan'
            }`}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            <span>{isPlaying ? 'Pause Sweep' : 'Spatial L-to-R Sweep'}</span>
          </button>
        </div>
      </div>

      {/* Hex Grid Map Visualization */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-6">
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span className="flex items-center gap-2">
            <Navigation className="w-4 h-4 text-cyan-400" />
            <span>Interactive Regional Grid (Click hex or trigger spatial sweep)</span>
          </span>
          <span className="font-mono text-cyan-400 font-semibold">
            Active Filter: {activeType.toUpperCase()} EXTREMES
          </span>
        </div>

        {/* Region Grid Display */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {SPM3_REGIONS.map((region, idx) => {
            const isScanning = isPlaying && scanIndex === idx;
            const isSelected = activeRegionId === region.id;

            let status = region.hotIncrease;
            if (activeType === 'precip') status = region.precipIncrease;
            if (activeType === 'drought') status = region.droughtIncrease;

            let bgColor = 'bg-red-950/40 border-red-500/50 text-red-300';
            if (status === 'medium') bgColor = 'bg-amber-950/40 border-amber-500/50 text-amber-300';
            if (status === 'low') bgColor = 'bg-slate-900/60 border-slate-800 text-slate-400';

            return (
              <div
                key={region.id}
                onClick={() => handleRegionClick(region)}
                className={`p-4 rounded-2xl border cursor-pointer transition-all duration-200 glass-panel-hover flex flex-col justify-between h-28 relative ${bgColor} ${
                  isScanning || isSelected ? 'ring-2 ring-cyan-400 glow-cyan scale-105' : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <span className="font-mono font-bold text-xs bg-slate-950/80 px-2 py-0.5 rounded-md text-white">
                    {region.code}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider font-bold">
                    {status}
                  </span>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-white leading-snug">{region.name}</h3>
                  <span className="text-[10px] font-mono text-slate-400 block mt-1">
                    Pan: {((region.x - 2) / 9 * 100 - 50).toFixed(0)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
